import { forwardRef, useId } from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Form primitives — lightweight wrapper (Faza 8 polish).
 *
 * Maqsad: ID, aria-describedby, error/helper layout — qo'lda yozish o'rniga
 * konsistent forma quyish.
 *
 * Pattern:
 *   <FormField>
 *     <FormLabel required>Email</FormLabel>
 *     <FormControl>
 *       <Input type="email" />
 *     </FormControl>
 *     <FormHelper>Bu yerda biz qabul qilamiz</FormHelper>
 *     <FormError>Email noto'g'ri</FormError>
 *   </FormField>
 *
 * React Hook Form bilan ishlaydi (caller `register` o'zi qo'shadi).
 */

// ----- Form (root form element) -----
export const Form = forwardRef<HTMLFormElement, React.FormHTMLAttributes<HTMLFormElement>>(
  ({ className, ...props }, ref) => (
    <form ref={ref} className={cn("flex flex-col gap-5", className)} {...props} />
  ),
);
Form.displayName = "Form";

// ----- FormField (single field + ID context) -----
interface FieldCtxValue {
  id: string;
  errorId: string;
  helperId: string;
  invalid: boolean;
}

import { createContext, useContext } from "react";

const FieldContext = createContext<FieldCtxValue | null>(null);

export function useFieldContext() {
  const ctx = useContext(FieldContext);
  return ctx;
}

export interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Manual id (default — useId) */
  id?: string;
  /** Field error mavjudligi (border + ring uchun) */
  invalid?: boolean;
}

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  ({ className, id, invalid = false, children, ...props }, ref) => {
    const reactId = useId();
    const fid = id ?? `field-${reactId}`;
    const value: FieldCtxValue = {
      id: fid,
      errorId: `${fid}-error`,
      helperId: `${fid}-helper`,
      invalid,
    };
    return (
      <FieldContext.Provider value={value}>
        <div ref={ref} className={cn("flex flex-col gap-1.5", className)} {...props}>
          {children}
        </div>
      </FieldContext.Provider>
    );
  },
);
FormField.displayName = "FormField";

// ----- FormLabel (re-export ergonomic; relies on Label primitive) -----
import { Label, type LabelProps } from "./Label";

export const FormLabel = forwardRef<HTMLLabelElement, LabelProps>(
  ({ ...props }, ref) => {
    const ctx = useFieldContext();
    return <Label ref={ref} htmlFor={ctx?.id} {...props} />;
  },
);
FormLabel.displayName = "FormLabel";

// ----- FormControl (clones child to inject id + aria) -----
import { cloneElement, isValidElement } from "react";

export interface FormControlProps {
  children: React.ReactElement;
}

export function FormControl({ children }: FormControlProps) {
  const ctx = useFieldContext();
  if (!isValidElement(children)) return children;
  if (!ctx) return children;

  const childProps = (children.props ?? {}) as Record<string, unknown>;
  const existingDescribedBy = (childProps["aria-describedby"] as string | undefined) ?? "";
  const describedBy = [existingDescribedBy, ctx.invalid ? ctx.errorId : ctx.helperId]
    .filter(Boolean)
    .join(" ") || undefined;

  return cloneElement(children, {
    id: (childProps.id as string | undefined) ?? ctx.id,
    "aria-describedby": describedBy,
    "aria-invalid": ctx.invalid ? true : undefined,
    invalid: ctx.invalid || (childProps.invalid as boolean | undefined),
  } as Record<string, unknown>);
}

// ----- FormHelper (small helper text) -----
export interface FormHelperProps extends React.HTMLAttributes<HTMLParagraphElement> {}

export const FormHelper = forwardRef<HTMLParagraphElement, FormHelperProps>(
  ({ className, children, ...props }, ref) => {
    const ctx = useFieldContext();
    if (!children) return null;
    return (
      <p
        ref={ref}
        id={ctx?.helperId}
        className={cn("text-[11px] leading-tight text-[var(--form-helper-fg)]", className)}
        {...props}
      >
        {children}
      </p>
    );
  },
);
FormHelper.displayName = "FormHelper";

// ----- FormError (error message under field) -----
export interface FormErrorProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /** Ikon ko'rsatish (default true) */
  showIcon?: boolean;
}

export const FormError = forwardRef<HTMLParagraphElement, FormErrorProps>(
  ({ className, children, showIcon = true, ...props }, ref) => {
    const ctx = useFieldContext();
    if (!children) return null;
    return (
      <p
        ref={ref}
        id={ctx?.errorId}
        role="alert"
        className={cn(
          "flex items-start gap-1 text-[11px] leading-tight text-[var(--form-error-fg)]",
          className,
        )}
        {...props}
      >
        {showIcon && <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" aria-hidden />}
        <span>{children}</span>
      </p>
    );
  },
);
FormError.displayName = "FormError";

// ----- FormErrorSummary (form yuqorisida barcha xato yig'masi) -----
export interface FormErrorSummaryProps {
  title?: string;
  errors: Array<{ field: string; message: string }>;
  className?: string;
}

export function FormErrorSummary({
  title = "Formada xatolar bor",
  errors,
  className,
}: FormErrorSummaryProps) {
  if (errors.length === 0) return null;
  return (
    <div
      role="alert"
      className={cn(
        "rounded-lg border px-3 py-2.5",
        "bg-[var(--form-error-bg)] border-[var(--form-error-border)]",
        className,
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertCircle className="h-4 w-4 text-[var(--form-error-fg)]" aria-hidden />
        <span className="text-sm font-medium text-[var(--form-error-fg)]">{title}</span>
      </div>
      <ul className="ml-6 list-disc space-y-0.5">
        {errors.map((e) => (
          <li key={e.field} className="text-xs text-[var(--form-error-fg)]/90">
            <span className="font-medium">{e.field}:</span> {e.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
